import { useEffect, useState } from "react";
import type { Options } from "react-markdown";
import { remarkAutolinkFallback } from "@/lib/remarkAutolinkFallback";

type RemarkPlugins = NonNullable<Options["remarkPlugins"]>;
type RemarkPlugin = RemarkPlugins[number];

let supportsRemarkGfmAutolink: boolean | null = null;
let cachedRemarkGfm: RemarkPlugin | null = null;
let cachedRemarkGfmPromise: Promise<RemarkPlugin | null> | null = null;

const canLoadRemarkGfm = () => {
  if (supportsRemarkGfmAutolink !== null) {
    return supportsRemarkGfmAutolink;
  }

  try {
    // `remark-gfm@4` pulls in a regex that Safari 16.4 and below cannot parse.
    new RegExp(
      "(?<=^|\\s|\\p{P}|\\p{S})([-.\\w+]+)@([-\\w]+(?:\\.[-\\w]+)+)",
      "gu",
    );
    supportsRemarkGfmAutolink = true;
  } catch {
    supportsRemarkGfmAutolink = false;
  }

  return supportsRemarkGfmAutolink;
};

const loadRemarkGfm = async () => {
  if (!canLoadRemarkGfm()) {
    return null;
  }

  if (cachedRemarkGfm) {
    return cachedRemarkGfm;
  }

  if (!cachedRemarkGfmPromise) {
    cachedRemarkGfmPromise = import("remark-gfm")
      .then((module) => {
        cachedRemarkGfm = module.default;
        return cachedRemarkGfm;
      })
      .catch(() => null);
  }

  return cachedRemarkGfmPromise;
};

export function useSafeRemarkPlugins(
  basePlugins: RemarkPlugins = [],
  fallbackPlugins: RemarkPlugins = [remarkAutolinkFallback],
) {
  const [remarkGfm, setRemarkGfm] = useState<RemarkPlugin | null>(
    () => cachedRemarkGfm,
  );

  useEffect(() => {
    let cancelled = false;

    loadRemarkGfm().then((plugin) => {
      if (!cancelled) {
        setRemarkGfm(() => plugin);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return remarkGfm ? [...basePlugins, remarkGfm] : [...basePlugins, ...fallbackPlugins];
}
